import React, { FC, useEffect, useRef, useState } from 'react'
import { Link, useTranslation } from 'gatsby-plugin-react-i18next'
import { useLocation } from '@reach/router'
import { motion, AnimatePresence } from 'framer-motion'
import { gsap } from 'gsap'
import { isInitialLoad } from 'src/helpers/animations'
import ThemeToggle from 'src/components/ui/ThemeToggle'
import { useAuth } from 'src/auth'
import { User } from 'lucide-react'

interface IBottomMenuProps {
  className?: string
}

interface IPillRect {
  left: number
  top: number
  width: number
  height: number
  opacity: number
}

let lastActiveRect: IPillRect | null = null

const AnimatedText: FC<{ children: React.ReactNode; isActive?: boolean }> = ({
  children,
  isActive,
}) => (
  <span className="relative block overflow-hidden leading-2">
    {/* First span: Normal flow, determines container size */}
    <span
      className={`block transition-transform duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] transform ${
        !isActive ? 'group-hover:-translate-y-full' : ''
      } will-change-transform [backface-visibility:hidden] [transform:translateZ(0)]`}
    >
      {children}
    </span>
    {/* Second span: Absolute, slides in from bottom */}
    {!isActive && (
      <span className="absolute inset-0 block transition-transform duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] transform translate-y-full group-hover:translate-y-0 will-change-transform [backface-visibility:hidden] [transform:translateZ(0)]">
        {children}
      </span>
    )}
  </span>
)

const BottomMenu: FC<IBottomMenuProps> = ({ className = '' }) => {
  const { t } = useTranslation('side-menu')
  const location = useLocation()
  const { user, loginWithGoogle, logout, deleteAccount } = useAuth()
  const [isVisible, setIsVisible] = useState(true)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuBarRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isSlides = location.pathname?.includes('/slides')
  const shouldAnimate = React.useMemo(() => !isSlides && isInitialLoad(), [isSlides])
  const isDesignSystem = location.pathname?.includes('/design-system')
  const isTechnologyRadar = location.pathname?.includes('/technology-radar')

  const initialAnimation = !hasMounted
    ? shouldAnimate
      ? { opacity: 0, y: 60 }
      : false
    : { opacity: 0, y: 60 }

  const menuItems = isDesignSystem
    ? [
        { name: '← mamf.pl', url: '/' },
        { name: 'home', url: '/design-system' },
        { name: 'docs', url: '/design-system/docs', partiallyActive: true },
        { name: 'components', url: '/design-system/components', partiallyActive: true },
      ]
    : [
        { name: t('home'), url: '/' },
        { name: t('sport'), url: '/sport', partiallyActive: true },
        { name: t('services'), url: '/services', partiallyActive: true },
        { name: t('case study'), url: '/case-studies', partiallyActive: true },
        { name: t('career'), url: '/career', partiallyActive: true },
      ]

  const dropdownItems = isDesignSystem
    ? [
        { name: 'home (design system)', url: '/design-system' },
        { name: 'docs (dokumentacja)', url: '/design-system/docs', partiallyActive: true },
        { name: 'components (katalog)', url: '/design-system/components', partiallyActive: true },
        { name: '← powrót do mamf.pl', url: '/' },
        { name: t('technology radar'), url: '/technology-radar', partiallyActive: true },
        { name: t('contact'), url: '/contact' },
      ]
    : [
        { name: t('technology radar'), url: '/technology-radar', partiallyActive: true },
        ...(!isSlides ? [{ name: t('iso certification'), url: '/iso' }] : []),
        { name: t('accessibility statement'), url: '/accessibility-statement' },
        { name: t('security'), url: '/security' },
        { name: t('privacy policy'), url: '/privacy-policy' },
        // { name: t('investor relations'), url: '/investor-relations' },
      ]

  const checkIsActive = (url: string, partiallyActive?: boolean) => {
    const rawPath = (location.pathname || '/').replace(/^\/(?:pl|en)(?=\/|$)/, '') || '/'
    const currentPath = rawPath.replace(/\/$/, '') || '/'
    const targetPath = url.replace(/\/$/, '') || '/'

    if (partiallyActive) {
      if (targetPath === '/') return currentPath === '/'
      return currentPath.startsWith(targetPath)
    }
    return currentPath === targetPath
  }

  const allMainItems = [
    ...menuItems,
    { name: t('contact'), url: '/contact', partiallyActive: true },
  ]

  const activeItem = allMainItems.find((item) =>
    checkIsActive(item.url, item.partiallyActive)
  )
  const activeUrl = activeItem?.url

  const handleLinkClick = (targetUrl: string) => {
    const targetEl = itemRefs.current.get(targetUrl)
    if (targetEl && menuBarRef.current && pillRef.current) {
      const menuBarRect = menuBarRef.current.getBoundingClientRect()
      const elRect = targetEl.getBoundingClientRect()
      const targetRect: IPillRect = {
        left: elRect.left - menuBarRect.left,
        top: elRect.top - menuBarRect.top,
        width: elRect.width,
        height: elRect.height,
        opacity: 1,
      }

      gsap.to(pillRef.current, {
        left: targetRect.left,
        top: targetRect.top,
        width: targetRect.width,
        height: targetRect.height,
        opacity: 1,
        duration: 0.45,
        ease: 'power3.out',
        overwrite: 'auto',
      })
    }
  }

  useEffect(() => {
    const updatePill = () => {
      if (!menuBarRef.current || !pillRef.current) return

      const activeEl = activeUrl ? itemRefs.current.get(activeUrl) : null
      if (!activeEl) {
        gsap.to(pillRef.current, { opacity: 0, duration: 0.25, overwrite: 'auto' })
        lastActiveRect = null
        return
      }

      const menuBarRect = menuBarRef.current.getBoundingClientRect()
      const elRect = activeEl.getBoundingClientRect()

      if (elRect.width === 0) {
        requestAnimationFrame(updatePill)
        return
      }

      const targetRect: IPillRect = {
        left: elRect.left - menuBarRect.left,
        top: elRect.top - menuBarRect.top,
        width: elRect.width,
        height: elRect.height,
        opacity: 1,
      }

      if (
        lastActiveRect &&
        lastActiveRect.opacity > 0 &&
        (Math.abs(lastActiveRect.left - targetRect.left) > 1 ||
          Math.abs(lastActiveRect.width - targetRect.width) > 1)
      ) {
        // Slide from previous page's position to current item!
        gsap.fromTo(
          pillRef.current,
          {
            left: lastActiveRect.left,
            top: lastActiveRect.top,
            width: lastActiveRect.width,
            height: lastActiveRect.height,
            opacity: 1,
          },
          {
            left: targetRect.left,
            top: targetRect.top,
            width: targetRect.width,
            height: targetRect.height,
            opacity: 1,
            duration: 0.45,
            ease: 'power3.out',
            overwrite: 'auto',
          }
        )
      } else if (!lastActiveRect) {
        gsap.set(pillRef.current, {
          left: targetRect.left,
          top: targetRect.top,
          width: targetRect.width,
          height: targetRect.height,
          opacity: 1,
        })
      } else if (!gsap.isTweening(pillRef.current)) {
        gsap.set(pillRef.current, {
          left: targetRect.left,
          top: targetRect.top,
          width: targetRect.width,
          height: targetRect.height,
          opacity: 1,
        })
      }

      lastActiveRect = targetRect
    }

    const rafId = requestAnimationFrame(updatePill)
    window.addEventListener('resize', updatePill)

    if (typeof document !== 'undefined' && document.fonts) {
      document.fonts.ready.then(updatePill)
    }

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updatePill)
      if (pillRef.current && menuBarRef.current) {
        const menuBarRect = menuBarRef.current.getBoundingClientRect()
        const pillBounding = pillRef.current.getBoundingClientRect()
        if (pillBounding.width > 0) {
          lastActiveRect = {
            left: pillBounding.left - menuBarRect.left,
            top: pillBounding.top - menuBarRect.top,
            width: pillBounding.width,
            height: pillBounding.height,
            opacity: 1,
          }
        }
      }
    }
  }, [activeUrl, isDesignSystem])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setIsUserMenuOpen(false)
      }
    }

    if (isDropdownOpen || isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen, isUserMenuOpen])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting)
      },
      {
        rootMargin: '0px 0px -5% 0px',
        threshold: 0,
      }
    )

    const contactSection = document.getElementById('contact-footer')
    if (contactSection) {
      observer.observe(contactSection)
    }

    return () => {
      if (contactSection) observer.unobserve(contactSection)
    }
  }, [])

  return (
    <div className="md:block hidden">
      <div
        className={`fixed bottom-8 xl:bottom-12 left-1/2 z-40 transform -translate-x-1/2 ${className}`}
        ref={dropdownRef}
      >
        <AnimatePresence>
          {isVisible && !isTechnologyRadar && (
            <motion.div
              key="bottom-menu-container"
              initial={initialAnimation}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="relative"
            >
              {/* Upward Dropdown Menu */}
              <AnimatePresence>
                {!isDesignSystem && isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: -12 }}
                    exit={{ opacity: 0, y: 15 }}
                    transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                    className="absolute bottom-full right-0 mb-4 w-64 origin-bottom z-50 shadow-elevation-highest bg-surface/80 dark:bg-surface/40 backdrop-blur-xl border border-outline-lower dark:border-outline/20 rounded-container-medium"
                  >
                    <div className="py-2 px-2 flex flex-col gap-1">
                      {dropdownItems.map((item, index) => {
                        const isActive = checkIsActive(item.url)
                        return (
                          <motion.div
                            key={item.url}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.04 + 0.1 }}
                          >
                            <Link
                              to={item.url}
                              activeClassName="active !opacity-100 !bg-surface shadow-sm"
                              className="text-on-surface font-palanquin font-bold transition-all duration-300 opacity-80 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 hover:bg-surface/80 w-full justify-start text-sm md:text-base group"
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              <AnimatedText isActive={isActive}>{item.name}</AnimatedText>
                            </Link>
                          </motion.div>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Upward User Dropdown Menu */}
              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: -12 }}
                    exit={{ opacity: 0, y: 15 }}
                    transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                    className="absolute bottom-full right-0 mb-4 w-64 origin-bottom z-50 shadow-elevation-highest bg-surface/80 dark:bg-surface/40 backdrop-blur-xl border border-outline-lower dark:border-outline/20 rounded-container-medium overflow-hidden"
                  >
                    {user ? (
                      <>
                        <div className="px-5 py-4 border-b border-outline-lower flex items-center gap-3">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={user.displayName}
                              referrerPolicy="no-referrer"
                              className="w-10 h-10 rounded-full object-cover shrink-0 ring-1 ring-outline-lower"
                            />
                          ) : (
                            <div
                              style={{ backgroundColor: user.color }}
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base shrink-0"
                            >
                              {user.initials || (user.displayName?.trim().split(/\s+/).pop()?.charAt(0) || 'G').toUpperCase()}
                            </div>
                          )}
                          <div className="flex flex-col min-w-0">
                            <span className="text-on-surface font-palanquin font-bold text-sm truncate">
                              {user.displayName || 'Użytkownik'}
                            </span>
                            <span className="text-on-surface-subtle font-palanquin text-xs truncate">
                              {user.email}
                            </span>
                          </div>
                        </div>

                        <div className="py-2 px-2 flex flex-col gap-1">
                          <Link
                            to="/slides"
                            className="text-on-surface font-palanquin font-bold transition-all duration-300 opacity-80 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 hover:bg-on-surface/10 w-full justify-start text-sm md:text-base group"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <AnimatedText>slides panel</AnimatedText>
                          </Link>

                          <button
                            type="button"
                            onClick={async () => {
                              setIsUserMenuOpen(false)
                              await logout()
                            }}
                            className="text-on-surface font-palanquin font-bold transition-all duration-300 opacity-80 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 hover:bg-on-surface/10 w-full justify-start text-sm md:text-base cursor-pointer group"
                          >
                            <AnimatedText>sign out</AnimatedText>
                          </button>

                          <button
                            type="button"
                            onClick={async () => {
                              if (
                                typeof window !== 'undefined' &&
                                window.confirm('Are you sure you want to delete your account? All session data will be permanently cleared.')
                              ) {
                                setIsUserMenuOpen(false)
                                await deleteAccount()
                              }
                            }}
                            className="text-error hover:text-error/80 font-palanquin font-bold transition-all duration-300 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] hover:bg-on-surface/10 w-full justify-start text-sm md:text-base cursor-pointer group"
                          >
                            <AnimatedText>delete account</AnimatedText>
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="py-2 px-2 flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={async () => {
                            setIsUserMenuOpen(false)
                            try {
                              await loginWithGoogle()
                            } catch {
                              if (typeof window !== 'undefined') {
                                window.location.href = '/slides'
                              }
                            }
                          }}
                          className="text-on-surface font-palanquin font-bold transition-all duration-300 opacity-80 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 hover:bg-on-surface/10 w-full justify-start text-sm md:text-base cursor-pointer group"
                        >
                          <AnimatedText>sign in with google</AnimatedText>
                        </button>
                        <Link
                          to="/slides"
                          className="text-on-surface font-palanquin font-bold transition-all duration-300 opacity-80 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 hover:bg-on-surface/10 w-full justify-start text-sm md:text-base group"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <AnimatedText>slides login panel</AnimatedText>
                        </Link>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Main Menu Bar */}
              <div
                ref={menuBarRef}
                className="bg-highlight-base dark:bg-surface/40 backdrop-blur-xl border border-outline-lower rounded-full shadow-elevation-highest flex items-center py-1.5 px-1.5 lg:py-1.5 lg:px-1.5 xl:py-2 xl:px-2 relative"
              >
                {/* Animated Sliding Pill Highlight */}
                <div
                  ref={pillRef}
                  className="absolute bg-elevation-inverse-lowest shadow-elevation-lowest rounded-full pointer-events-none z-0 will-change-transform"
                  style={
                    lastActiveRect
                      ? {
                          opacity: lastActiveRect.opacity,
                          top: lastActiveRect.top,
                          left: lastActiveRect.left,
                          width: lastActiveRect.width,
                          height: lastActiveRect.height,
                        }
                      : {
                          opacity: 0,
                          top: 0,
                          left: 0,
                          width: 0,
                          height: 0,
                        }
                  }
                />

                {menuItems.map(({ url, name, partiallyActive }, index) => {
                  const isActive = checkIsActive(url, partiallyActive)
                  return (
                    <React.Fragment key={name}>
                      {isDesignSystem && index === 1 && (
                        <div className="w-px h-5 lg:h-5 xl:h-6 bg-outline-lower opacity-50 mx-1.5 lg:mx-1.5 xl:mx-2 z-10" />
                      )}
                      <div
                        ref={(el) => {
                          if (el) itemRefs.current.set(url, el)
                          else itemRefs.current.delete(url)
                        }}
                        className="relative z-10 flex items-center"
                      >
                        <Link
                          to={url}
                          partiallyActive={partiallyActive}
                          onClick={() => handleLinkClick(url)}
                          className={`text-on-surface font-palanquin font-bold transition-opacity duration-300 relative flex items-center px-3 py-1.5 lg:px-3 lg:py-1.5 xl:px-4 xl:py-2 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 text-body-medium lg:text-body-medium xl:text-base whitespace-nowrap group ${
                            isActive ? 'opacity-100' : 'opacity-70'
                          }`}
                        >
                          <AnimatedText isActive={isActive}>{name}</AnimatedText>
                        </Link>
                      </div>
                    </React.Fragment>
                  )
                })}

                <div className="w-px h-5 lg:h-5 xl:h-6 bg-outline-lower opacity-50 mx-1.5 lg:mx-1.5 xl:mx-2 z-10" />

                {(() => {
                  const isContactActive = checkIsActive('/contact', true)
                  return (
                    <div
                      ref={(el) => {
                        if (el) itemRefs.current.set('/contact', el)
                        else itemRefs.current.delete('/contact')
                      }}
                      className="relative z-10 flex items-center"
                    >
                      <Link
                        to="/contact"
                        partiallyActive
                        onClick={() => handleLinkClick('/contact')}
                        className={`text-on-surface font-palanquin font-bold transition-opacity duration-300 relative flex items-center px-3 py-1.5 lg:px-3 lg:py-1.5 xl:px-4 xl:py-2 rounded-full [webkit-font-smoothing:antialiased] hover:opacity-100 text-body-medium lg:text-body-medium xl:text-base whitespace-nowrap flex items-center gap-1.5 group ${
                          isContactActive ? 'opacity-100' : 'opacity-70'
                        }`}
                      >
                        <AnimatedText isActive={isContactActive}>
                          {t('contact')}
                        </AnimatedText>
                      </Link>
                    </div>
                  )
                })()}

                {!isDesignSystem && (
                  <>
                    <div className="w-px h-5 lg:h-5 xl:h-6 bg-outline-lower opacity-50 mx-1.5 lg:mx-1.5 xl:mx-2 z-10" />

                    {/* Dropdown Toggle */}
                    <button
                      onClick={() => {
                        setIsDropdownOpen(!isDropdownOpen)
                        setIsUserMenuOpen(false)
                      }}
                      className="text-on-surface opacity-70 hover:opacity-100 transition-opacity p-1.5 lg:p-1.5 xl:p-2 rounded-full flex items-center justify-center min-w-[34px] lg:min-w-[34px] xl:min-w-[40px] z-10 cursor-pointer"
                      aria-label="More options"
                    >
                      <motion.div
                        animate={{ rotate: isDropdownOpen ? 45 : 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5"
                        >
                          <path
                            d="M10 5V15M5 10H15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </motion.div>
                    </button>
                  </>
                )}

                <div className="w-px h-5 lg:h-5 xl:h-6 bg-outline-lower opacity-50 mx-1.5 lg:mx-1.5 xl:mx-2 z-10" />
                <div className="z-10 flex items-center justify-center min-w-[34px] lg:min-w-[34px] xl:min-w-[40px]">
                  <ThemeToggle
                    minimal
                    textColor="text-on-surface"
                    className="opacity-70 hover:opacity-100 p-1.5 lg:p-1.5 xl:p-2 min-w-[34px] lg:min-w-[34px] xl:min-w-[40px]"
                    iconClassName="w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5"
                  />
                </div>

                {/* Temporarily hidden - direct link access only */}
                {false && user && (
                  <>
                    <div className="w-px h-5 lg:h-5 xl:h-6 bg-outline-lower opacity-50 mx-1.5 lg:mx-1.5 xl:mx-2 z-10" />

                    <div className="z-10 flex items-center justify-center min-w-[34px] lg:min-w-[34px] xl:min-w-[40px]">
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen((prev) => !prev)
                          setIsDropdownOpen(false)
                        }}
                        className="p-0.5 rounded-full bg-layer-low text-on-surface border border-outline-lower shadow-elevation-lowest cursor-pointer flex items-center justify-center focus:outline-none"
                        aria-label="Profil użytkownika"
                      >
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName}
                            referrerPolicy="no-referrer"
                            className="w-6 h-6 lg:w-6 lg:h-6 xl:w-7 xl:h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div
                            style={{ backgroundColor: user.color }}
                            className="w-6 h-6 lg:w-6 lg:h-6 xl:w-7 xl:h-7 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-xs xl:text-sm shrink-0"
                          >
                            {user.initials || (user.displayName?.trim().split(/\s+/).pop()?.charAt(0) || 'G').toUpperCase()}
                          </div>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default BottomMenu
